import React from 'react';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';

const languages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'pt', name: 'Português', flag: '🇧🇷' }
];

export default function LanguageSelector({ variant = 'default', onLanguageChange, selectedLanguage, immediateApply = true }) {
  const { i18n } = useTranslation();
  
  // Use passed selectedLanguage (controlled) or fallback to i18n.language
  const currentLang = selectedLanguage || i18n.language;

  const handleChange = (e) => {
    const lang = e.target.value;
    if (immediateApply) {
        i18n.changeLanguage(lang);
    }
    // Mark that the user has EXPLICITLY chosen a language on this
    // device. ProtectedRoute reads this marker to know whether the
    // server-side default may safely apply — otherwise the guest
    // login (server-default = 'en') would clobber a Spanish
    // speaker's pre-login choice on every reload.
    try { localStorage.setItem('eili-lang-explicit', '1'); } catch { /* private mode */ }
    if (onLanguageChange) onLanguageChange(lang);
  };

  if (variant === 'button-group') {
     return (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {languages.map((lang) => (
                <button
                    key={lang.code}
                    onClick={() => handleChange({ target: { value: lang.code } })}
                    className={`p-4 rounded-3xl border-2 transition-all font-medium flex items-center gap-3 ${
                        currentLang.startsWith(lang.code)
                            ? 'border-[var(--theme-accent)] bg-[var(--theme-accentLight)] theme-text'
                            : 'theme-border theme-surface theme-text-secondary hover:border-[var(--theme-accent)]'
                    }`}
                >
                    <span className="text-2xl">{lang.flag}</span>
                    <span className="text-base">{lang.name}</span>
                </button>
            ))}
        </div>
     );
  }

  return (
    <div className="relative inline-block">
        <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 pointer-events-none" style={{ color: 'var(--theme-textSecondary)' }} />
        <select
            value={currentLang.split('-')[0]} 
            onChange={handleChange}
            className="pl-9 pr-8 py-2.5 rounded-full text-sm font-medium appearance-none cursor-pointer transition-all focus:outline-none"
            style={{ background: 'transparent', color: 'var(--theme-text)', border: 'none' }}
        >
            {languages.map((lang) => (
                <option key={lang.code} value={lang.code} style={{ background: 'var(--theme-surface)', color: 'var(--theme-text)' }}>
                    {lang.name}
                </option>
            ))}
        </select>
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
            <svg className="h-4 w-4" style={{ color: 'var(--theme-textSecondary)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
        </div>
    </div>
  );
}
